import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  VStack,
  HStack,
  Checkbox,
  IconButton,
  Button,
  Divider,
  Spinner,
  useToast,
  Input,
} from '@chakra-ui/react';
import { FaTrash, FaCheck } from 'react-icons/fa';
import { useBellNotifications } from '../hooks/useBellNotifications';
import { useChat } from '../features/chat';
import { UsersTab } from '../features/chat/components/UsersTab';
import { ChatMessages } from '../features/chat/components/ChatMessages';
import UserAvatar from '../components/UserAvatar';
import { useUserStore } from '../store/userStore';
import { GroupChatDialog } from '../features/chat/components/GroupChatDialog';
import { GroupChatMembersDialog } from '../features/chat/components/GroupChatMembersDialog';
import { FaArrowLeft, FaUsers } from 'react-icons/fa';
import { useDisclosure } from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';
import PageTitle from '../components/PageTitle';

const darkBg = '#181A20';
const darkTableBg = '#23262F';
const darkText = 'white';
const darkSecondary = 'gray.400';
const PRIMARY_GREEN = '#002e0b';
const hoverBg = '#111';
const messageBg = '#111';
const userMessageBg = PRIMARY_GREEN;
const inputBg = '#222';
const borderColor = PRIMARY_GREEN;

// Messages Tab Component
const MessagesTab: React.FC = () => {
  const { 
    messages, 
    activeRoom, 
    setActiveRoom, 
    sendMessage, 
    rooms, 
    users, 
    createDirectChat, 
    joinDirectChat, 
    currentUserId, 
    totalUnread, 
    markedUsers, 
    joinRoom, 
    groupChats, 
    setRooms 
  } = useChat();
  
  const { user: currentUser } = useUserStore();
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const { isOpen: isGroupChatOpen, onOpen: onGroupChatOpen, onClose: onGroupChatClose } = useDisclosure();
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [groupChatName, setGroupChatName] = useState('');
  const [groupChatMembers, setGroupChatMembers] = useState<any[]>([]);
  const [isGroupChatMembersOpen, setIsGroupChatMembersOpen] = useState(false);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;
    
    // Verify the user is actually in this room
    const room = rooms.find(r => r.id === selectedRoom);
    if (!room) {
      console.error('Cannot send message: Room not found');
      return;
    }
    
    // Check if user is a member of the room
    const isMember = room.members?.includes(currentUserId || '');
    if (!isMember) {
      console.error('Cannot send message: User is not a member of this room');
      return;
    }
    
    sendMessage(newMessage, selectedRoom);
    setRooms(prev => prev.map(room => room.id === selectedRoom ? { ...room, last_message_at: new Date().toISOString() } : room));
    setNewMessage('');
  };

  const handleBackClick = () => {
    setActiveRoom(null);
    setIsGroupChat(false);
    setGroupChatMembers([]);
    setGroupChatName('');
    setSelectedRoom(null);
  };

  const handleCreateGroupChat = (userIds: string[], chatName: string) => {
    if (userIds.length === 0 || !chatName) return;
    const allUserIds = [...new Set([currentUser?.id, ...userIds])];
    joinRoom('', `${chatName}`, 'group', 'Group chat', allUserIds);
    setIsGroupChat(true);
    setGroupChatName(chatName);
  };

  const joinExistingGroupChat = (chatRoomId: string, members: string[], chatName: string, users: any[]) => {
    // Clear any previous state
    setIsGroupChat(true);
    setGroupChatName(chatName);
    const otherUsers = users.filter((user: any) => user.id !== currentUser?.id);
    setGroupChatMembers(otherUsers);
    setSelectedRoom(chatRoomId);
    joinRoom(chatRoomId, '', 'group', '', members);
    const room = rooms.find(r => r.id === chatRoomId);
    if (room?.unread) {
      setRooms(prev => prev.map(room => room.id === chatRoomId ? { ...room, unread: false } : room));
    }
  };

  const handleJoinDirectChat = (roomId: string) => {
    joinDirectChat(roomId);
    const room = rooms.find(r => r.id === roomId);
    if (room?.unread) {
      setRooms(prev => prev.map(room => room.id === roomId ? { ...room, unread: false } : room));
    }
    // Clear group chat state when switching to direct chat
    setIsGroupChat(false);
    setGroupChatMembers([]);
    setGroupChatName('');
    setSelectedRoom(roomId);
  };

  useEffect(() => {
    setFilteredUsers(users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.username?.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }));
  }, [searchQuery, users]);

  useEffect(() => {
    if (activeRoom) {
      setSelectedRoom(activeRoom);
      // Determine if it's a group chat or direct chat based on the room
      const room = rooms.find(r => r.id === activeRoom);
      if (room) {
        if (room.type === 'group' || room.type === 'league') {
          setIsGroupChat(true);
          // Set group chat name and members if available
          if (room.name) setGroupChatName(room.name);
          if (room.users) {
            const otherUsers = room.users.filter((user: any) => user.id !== currentUser?.id);
            setGroupChatMembers(otherUsers);
          }
        } else {
          setIsGroupChat(false);
          setGroupChatMembers([]);
          setGroupChatName('');
        }
      }
    }
  }, [activeRoom, rooms, currentUser?.id]);

  const pickPalette = (name: string) => {
    const colorPalette = ["red", "blue", "green", "yellow", "purple", "orange"];
    const index = name.charCodeAt(0) % colorPalette.length;
    return colorPalette[index];
  };

  return (
    <Flex h="100%" bg={darkTableBg} borderRadius="lg" overflow="hidden">
      {/* Left Panel - Conversation List */}
      <Box w="350px" borderRight="1px solid" borderColor="gray.700" display="flex" flexDirection="column">
        <UsersTab
          markedUsers={markedUsers}
          users={filteredUsers}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onUserSelect={(userId) => createDirectChat(userId)}
          hoverBg={hoverBg}
          textColor={darkText}
          secondaryText={darkSecondary}
          inputBg={inputBg}
          onGroupChatOpen={onGroupChatOpen}
          pickPalette={pickPalette}
          groupChats={groupChats}
          rooms={rooms}
          joinExistingGroupChat={joinExistingGroupChat}
          onJoinDirectChat={handleJoinDirectChat}
          selectedRoom={selectedRoom}
        />
      </Box>

      {/* Right Panel - Chat Messages */}
      <Box flex="1" display="flex" flexDirection="column">
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <Flex
              align="center"
              justify="space-between"
              p={4}
              borderBottom="1px solid"
              borderColor="gray.700"
              bg={darkBg}
            >
              <Flex align="center" gap={3}>
                {(() => {
                  const room = rooms.find(r => r.id === selectedRoom);
                  const isRoomGroupChat = room && (room.type === 'group' || room.type === 'league');
                  
                  if (isRoomGroupChat && room?.users && room.users.length > 0) {
                    const otherUsers = room.users.filter((user: any) => user.id !== currentUserId);
                    return (
                      <HStack gap={2}>
                        {otherUsers.slice(0, 3).map((member: any) => (
                          <UserAvatar key={member.id} user={member} size="sm" canEdit={false} />
                        ))}
                        {otherUsers.length > 3 && (
                          <IconButton
                            aria-label="View all group members"
                            variant="ghost"
                            bg="transparent"
                            onClick={() => setIsGroupChatMembersOpen(true)}
                            color={darkText}
                            size="sm"
                            fontSize="lg"
                            _hover={{ bg: hoverBg }}
                          >
                            ...
                          </IconButton>
                        )}
                      </HStack>
                    );
                  }
                  return null;
                })()}
                <Text fontWeight="medium" color={darkText}>
                  {(() => {
                    const room = rooms.find(r => r.id === selectedRoom);
                    if (!room) return 'Chat';
                    
                    if (room.type === 'group' || room.type === 'league') {
                      return room.name || 'Group Chat';
                    }
                    
                    const otherUserId = room.members?.find((id: string) => id !== currentUserId);
                    const otherUser = users.find(u => u.id === otherUserId);
                    return otherUser?.username || otherUser?.email || 'Chat';
                  })()}
                </Text>
              </Flex>
            </Flex>

            {/* Chat Messages */}
            <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
              {(() => {
                const room = rooms.find(r => r.id === selectedRoom);
                const isRoomGroupChat = room && (room.type === 'group' || room.type === 'league');
                const roomGroupMembers = room?.users?.filter((user: any) => user.id !== currentUserId) || [];
                
                return (
                  <ChatMessages
                    messages={messages}
                    selectedRoom={selectedRoom}
                    currentUserId={currentUserId}
                    newMessage={newMessage}
                    onNewMessageChange={setNewMessage}
                    onSendMessage={handleSendMessage}
                    bgColor={darkBg}
                    messageBg={messageBg}
                    userMessageBg={userMessageBg}
                    textColor={darkText}
                    secondaryText={darkSecondary}
                    inputBg={inputBg}
                    borderColor={borderColor}
                    PRIMARY_GREEN={PRIMARY_GREEN}
                    isGroupChat={isRoomGroupChat || false}
                    groupChatMembers={roomGroupMembers}
                  />
                );
              })()}
            </Box>
          </>
        ) : (
          <Flex align="center" justify="center" h="100%" color={darkSecondary}>
            <Text>Select a conversation to start messaging</Text>
          </Flex>
        )}
      </Box>

      <GroupChatDialog
        isOpen={isGroupChatOpen}
        onClose={onGroupChatClose}
        users={users}
        onCreateGroupChat={handleCreateGroupChat}
        bgColor={darkBg}
        textColor={darkText}
        secondaryText={darkSecondary}
        inputBg={inputBg}
        hoverBg={hoverBg}
        PRIMARY_GREEN={PRIMARY_GREEN}
        rooms={rooms}
        onJoinDirectChat={handleJoinDirectChat}
        createDirectChat={createDirectChat}
      />
      <GroupChatMembersDialog
        isOpen={isGroupChatMembersOpen}
        onClose={() => setIsGroupChatMembersOpen(false)}
        groupChatMembers={groupChatMembers}
      />
    </Flex>
  );
};

const MessagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  
  // Determine default tab based on URL
  const defaultTabIndex = location.pathname === '/notifications' ? 1 : 0;
  const [tabIndex, setTabIndex] = useState(defaultTabIndex);
  
  // Update tab when URL changes
  useEffect(() => {
    if (location.pathname === '/notifications') {
      setTabIndex(1);
    } else if (location.pathname === '/messages') {
      setTabIndex(0);
    }
  }, [location.pathname]);

  // Handle tab change and update URL
  const handleTabChange = (index: number) => {
    setTabIndex(index);
    if (index === 0) {
      navigate('/messages', { replace: true });
    } else {
      navigate('/notifications', { replace: true });
    }
  };

  // Notifications tab state
  const { notifications, loading, error, markAsRead, markAllAsRead, deleteNotification, deleteNotifications, refetch } = useBellNotifications();
  const [selectedNotifications, setSelectedNotifications] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Notifications are already filtered on the backend (deleted ones are removed)
  const activeNotifications = useMemo(() => {
    return notifications;
  }, [notifications]);

  // Memoize formatted notifications
  const formattedNotifications = useMemo(() => {
    return activeNotifications.map(notification => ({
      ...notification,
      formattedDate: new Date(notification.created_at).toLocaleDateString(),
      formattedTime: new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));
  }, [activeNotifications]);

  // Handle individual checkbox toggle
  const handleToggleNotification = useCallback((notificationId: number) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedNotifications(new Set(formattedNotifications.map(n => n.id)));
    } else {
      setSelectedNotifications(new Set());
    }
  }, [formattedNotifications]);

  // Delete notification handler
  const handleDeleteNotification = useCallback(async (notificationId: number) => {
    try {
      await deleteNotification(notificationId);
      toast({
        title: 'Notification deleted',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error deleting notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [deleteNotification, toast]);

  // Delete selected notifications
  const handleDeleteSelected = useCallback(async () => {
    if (selectedNotifications.size === 0) return;
    
    setIsDeleting(true);
    try {
      const ids = Array.from(selectedNotifications);
      await deleteNotifications(ids);
      setSelectedNotifications(new Set());
      toast({
        title: `${ids.length} notification(s) deleted`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error deleting notifications',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedNotifications, deleteNotifications, toast]);

  // Mark selected as read
  const handleMarkSelectedAsRead = useCallback(async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      await Promise.all(Array.from(selectedNotifications).map(id => markAsRead(id)));
      setSelectedNotifications(new Set());
      toast({
        title: `${selectedNotifications.size} notification(s) marked as read`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error marking notifications as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [selectedNotifications, markAsRead, toast]);

  const allSelected = formattedNotifications.length > 0 && selectedNotifications.size === formattedNotifications.length;
  const someSelected = selectedNotifications.size > 0 && selectedNotifications.size < formattedNotifications.length;

  return (
    <Box bg={darkBg} h="100vh" color={darkText} p={6} display="flex" flexDirection="column" overflow="hidden" style={{ overflowY: 'hidden', position: 'relative' }}>
      <PageTitle>Messages and Notifications</PageTitle>
      <Tabs index={tabIndex} onChange={handleTabChange} colorScheme="blue" variant="enclosed" mt={4} display="flex" flexDirection="column" flex="1" overflow="hidden">
        <TabList>
          <Tab>Messages</Tab>
          <Tab>Notifications</Tab>
        </TabList>

        <TabPanels flex="1" overflow="hidden" display="flex" flexDirection="column">
          {/* Messages Tab */}
          <TabPanel p={0} pt={4} flex="1" overflow="hidden" display="flex" flexDirection="column">
            <MessagesTab />
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel p={0} pt={4} flex="1" overflow="hidden" display="flex" flexDirection="column">
            <Box bg={darkTableBg} borderRadius="lg" p={4} flex="1" overflowY="auto">
              {/* Header with actions */}
              <Flex justify="space-between" align="center" mb={4}>
                <HStack spacing={4}>
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    colorScheme="green"
                  >
                    <Text fontSize="sm" color={darkText}>Select All</Text>
                  </Checkbox>
                  
                  {selectedNotifications.size > 0 && (
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="red"
                        leftIcon={<FaTrash />}
                        onClick={handleDeleteSelected}
                        isLoading={isDeleting}
                        disabled={isDeleting}
                      >
                        DELETE SELECTED ({selectedNotifications.size})
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<FaCheck />}
                        onClick={handleMarkSelectedAsRead}
                      >
                        MARK AS READ ({selectedNotifications.size})
                      </Button>
                    </HStack>
                  )}
                </HStack>

                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={markAllAsRead}
                  disabled={loading}
                >
                  Mark All as Read
                </Button>
              </Flex>

              <Divider borderColor="gray.700" mb={4} />

              {/* Notifications List */}
              {loading ? (
                <Flex justify="flex-start" align="center" py={8}>
                  <Spinner color="green.400" />
                  <Text ml={4} color={darkSecondary}>Loading notifications...</Text>
                </Flex>
              ) : error ? (
                <Box p={4}>
                  <Text color="red.400">Error loading notifications: {error}</Text>
                </Box>
              ) : formattedNotifications.length === 0 ? (
                <Box p={8} textAlign="left">
                  <Text color={darkSecondary}>No notifications</Text>
                </Box>
              ) : (
                <VStack align="stretch" spacing={0}>
                  {formattedNotifications.map((notification) => {
                    const isSelected = selectedNotifications.has(notification.id);
                    return (
                      <Box key={notification.id}>
                        <Flex
                          align="flex-start"
                          p={3}
                          bg={notification.is_read ? 'transparent' : 'rgba(255, 255, 255, 0.05)'}
                          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                          borderRadius="md"
                          cursor="pointer"
                          onClick={() => {
                            if (!notification.is_read) {
                              markAsRead(notification.id);
                            }
                            if (notification.linkUrl) {
                              navigate(notification.linkUrl);
                            }
                          }}
                        >
                          <Checkbox
                            isChecked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleNotification(notification.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            colorScheme="green"
                            mr={3}
                            mt={1}
                          />
                          
                          <Box flex="1" textAlign="left">
                            {!notification.is_read && (
                              <Text fontSize="xs" color="red.400" fontWeight="bold" mb={1} textAlign="left">
                                NEW
                              </Text>
                            )}
                            <Text
                              fontSize="sm"
                              color={notification.is_read ? darkSecondary : darkText}
                              fontWeight={notification.is_read ? 'normal' : 'medium'}
                              textAlign="left"
                            >
                              {notification.processedMessage}
                            </Text>
                            <Text fontSize="xs" color={darkSecondary} mt={1} textAlign="left">
                              {notification.formattedDate} at {notification.formattedTime}
                            </Text>
                          </Box>

                          <IconButton
                            aria-label="Delete notification"
                            icon={<FaTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            _hover={{ bg: 'rgba(255, 0, 0, 0.2)' }}
                          />
                        </Flex>
                        <Divider borderColor="gray.700" />
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default MessagesPage;
